// src/components/intake/intakeRules.js
//
// The client's mirror of each page's "answered" rule (spec § The seven pages),
// used only to enable Next and choose the status line; the server's check is
// the one that counts. Rules are chosen by the `kind` each served page carries
// (backend plan ruling 3); the order of the pages lives only in the served
// `pages` array.
//
// A draft is the form's state; `bodyOf` turns it into the POST body
// (§ POST /api/onboarding/intake/:pageKey).

// The status line's copy key by kind, the server's names verbatim (the same
// key the server sends with 400 not_answered).
export const STATUS_KEYS = {
  rank: "pickAtLeastOne",
  multi: "pickAtLeastOne",
  twoPart: "pickAtLeastOne",
  multiExclusive: "pickAtLeastOne",
  amounts: "addAnAmount",
  aboutYou: "answerAtLeastOne",
};

// Q1: 0–3 seeded options in rank order.
export const MAX_RANKED = 3;
// Q3: the two records, in the order the page's questions are served.
export const PART_KEYS = ["shortTerm", "longTerm"];
// Q4: seeded option strings the rule names (the server validates the same).
export const Q4_EXCLUSIVE = "Nothing right now";
export const Q4_REVEALS = "Buying something big (car, home, property, etc.)";

// Q5, Q6: the web rule the legacy wizard used, as the server checks it
// after stripping "$", commas and spaces.
const AMOUNT_RE = /^\d{0,10}(\.\d{0,2})?$/;
export const normaliseAmount = (value) =>
  String(value ?? "").replace(/[$,\s]/g, "");
// The amount as a number, or NaN when it is empty or not a valid amount.
export const amountValue = (value) => {
  const s = normaliseAmount(value);
  return s !== "" && AMOUNT_RE.test(s) ? Number(s) : NaN;
};
// "7400" → "7,400"; "2280.5" → "2,280.50". Anything else is shown as given.
export const formatAmount = (value) => {
  const n = amountValue(value);
  if (Number.isNaN(n)) return String(value ?? "");
  const cents = n % 1 !== 0;
  return n.toLocaleString("en-US", {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2,
  });
};
// A computed total as money: rounded to cents (no float noise), with separators.
export const formatMoney = (n) => {
  const cents = Math.round(n * 100) / 100;
  return cents.toLocaleString("en-US", {
    minimumFractionDigits: cents % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};
const positive = (line) => line.ticked && amountValue(line.amount) > 0;
// Q7: the four free-text answers, in the order of the seeded prompts after
// the switch's question.
export const ABOUT_YOU_FIELDS = ["rely", "life", "nonFinancial", "talkFirst"];

const text = (s) => (s ?? "").trim();
const orNull = (s) => text(s) || null;
const picked = (part) => part.selected.length > 0 || Boolean(text(part.other));

const RULES = {
  rank: {
    draft: (page, a) => ({ ranked: a?.ranked ?? [], other: a?.other ?? "" }),
    body: (d) => ({ ranked: d.ranked, other: orNull(d.other) }),
    answered: (d) => d.ranked.length > 0 || Boolean(text(d.other)),
  },
  multi: {
    draft: (page, a) => ({
      selected: a?.selected ?? [],
      other: a?.other ?? "",
    }),
    body: (d) => ({ selected: d.selected, other: orNull(d.other) }),
    answered: picked,
  },
  twoPart: {
    draft: (page, a) =>
      Object.fromEntries(
        PART_KEYS.map((k) => [
          k,
          { selected: a?.[k]?.selected ?? [], other: a?.[k]?.other ?? "" },
        ]),
      ),
    body: (d) =>
      Object.fromEntries(
        PART_KEYS.map((k) => [
          k,
          { selected: d[k].selected, other: orNull(d[k].other) },
        ]),
      ),
    answered: (d) => PART_KEYS.some((k) => picked(d[k])),
  },
  multiExclusive: {
    draft: (page, a) => ({
      selected: a?.selected ?? [],
      plans: a?.plans ?? "",
    }),
    body: (d) => ({
      selected: d.selected,
      plans: d.selected.includes(Q4_REVEALS) ? orNull(d.plans) : null,
    }),
    answered: (d) => d.selected.length > 0,
  },
  amounts: {
    // One line per seeded option, in the served order.
    draft: (page, a) => ({
      lines: Object.fromEntries(
        page.questions[0].options.map((name) => {
          const saved = a?.lines?.find((l) => l.name === name);
          return [
            name,
            {
              ticked: Boolean(saved),
              amount: saved ? formatAmount(saved.amount) : "",
            },
          ];
        }),
      ),
    }),
    body: (d) => ({
      lines: Object.entries(d.lines)
        .filter(([, line]) => line.ticked)
        .map(([name, line]) => ({
          name,
          amount: normaliseAmount(line.amount),
        })),
    }),
    answered: (d) => Object.values(d.lines).some(positive),
  },
  aboutYou: {
    draft: (page, a) => ({
      workedWithAdvisor: a?.workedWithAdvisor ?? null,
      ...Object.fromEntries(ABOUT_YOU_FIELDS.map((f) => [f, a?.[f] ?? ""])),
    }),
    body: (d) => ({
      workedWithAdvisor: d.workedWithAdvisor,
      ...Object.fromEntries(ABOUT_YOU_FIELDS.map((f) => [f, text(d[f])])),
    }),
    // The switch alone does not answer the page.
    answered: (d) => ABOUT_YOU_FIELDS.some((f) => Boolean(text(d[f]))),
  },
};

const rule = (page) => RULES[page.kind];
export const draftFrom = (page, answer) =>
  rule(page).draft(page, answer ?? null);
export const bodyOf = (page, draft) => rule(page).body(draft, page);
export const isAnswered = (page, draft) => rule(page).answered(draft, page);
// The status line's key in STATUS_NEED: the page's own when the server
// serves one, else the key for its kind.
export const statusKeyOf = (page) => page.statusKey ?? STATUS_KEYS[page.kind];
// An empty form: what a clearing save sends (Questions page only, decision 16).
export const isCleared = (page, draft) =>
  JSON.stringify(bodyOf(page, draft)) ===
  JSON.stringify(bodyOf(page, draftFrom(page, null)));
// Q5/Q6: a ticked line without an amount above 0 blocks the save, with the
// line error under that line ({ [option]: true }). Other kinds: none.
export const lineErrorsOf = (page, draft) =>
  page.kind !== "amounts"
    ? {}
    : Object.fromEntries(
        Object.entries(draft.lines)
          .filter(([, line]) => line.ticked && !positive(line))
          .map(([name]) => [name, true]),
      );

// Ticking an option in a set: add or remove it, keeping the served order.
export const toggled = (options, selected, option) =>
  selected.includes(option)
    ? selected.filter((o) => o !== option)
    : options.filter((o) => o === option || selected.includes(o));
